<template>
    <v-container class="py-12">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Custos de veículos</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Home', to: '/home' },
                            { text: 'Financeiro', to: '/financeiro', exact: true },
                            { text: 'Custos de veículos', disabled: true }
                        ]" />
                    </div>
                    <div class="button-group">
                        <v-btn outlined color="primary" elevation="0" :loading="exporting" @click="exportOverview">
                            <v-icon left small>mdi-file-excel-outline</v-icon>Exportar
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-card class="mb-4">
            <v-card-text>
                <v-row dense align="center">
                    <v-col cols="12" sm="6" md="2">
                        <v-text-field dense outlined hide-details type="date" label="De" v-model="filter.start" />
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                        <v-text-field dense outlined hide-details type="date" label="Até" v-model="filter.end" />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-text-field dense outlined hide-details clearable label="Frota, placa ou modelo"
                            prepend-inner-icon="mdi-magnify" v-model="filter.search" @keyup.enter="load" />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-checkbox dense hide-details class="mt-0" label="Com movimento" v-model="filter.onlyActive" @change="load" />
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center flex-wrap" style="gap: 4px">
                        <v-btn color="primary" elevation="0" :loading="loading" @click="load">Filtrar</v-btn>
                        <v-btn small text color="primary" @click="setMonth(0)">Este mês</v-btn>
                        <v-btn small text color="primary" @click="setMonth(-1)">Mês passado</v-btn>
                        <v-btn small text color="primary" @click="setYear">Ano</v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <template v-if="result">
            <div class="stat-grid mb-4">
                <stat-card label="Custo total" :value="money(totals.cost)"
                    :hint="`${totals.trips} viagens · ${totals.cost_per_trip !== null ? money(totals.cost_per_trip) + ' por viagem' : 'sem viagens'}`" />
                <stat-card label="Receita (viagens finalizadas)" :value="money(totals.revenue)" color="#1450b5" hint="valor faturado dos passageiros" />
                <stat-card label="Margem" :value="money(totals.margin)" :color="totals.margin < 0 ? '#d32f2f' : '#2e7d32'"
                    :hint="totals.margin_percent !== null ? percent(totals.margin_percent) + ' da receita' : '-'" />
                <stat-card label="Custo por km" :value="totals.cost_per_km !== null ? money(totals.cost_per_km) : '-'"
                    :hint="`${number(totals.km)} km nos abastecimentos`" />
            </div>

            <v-card class="section-card mb-4">
                <v-card-title>Composição do custo</v-card-title>
                <v-card-text>
                    <div class="composition-bar" v-if="totals.cost > 0">
                        <div v-for="part in composition" :key="part.key" :style="{ width: part.percent + '%', background: part.color }"
                            :title="`${part.label}: ${money(part.value)}`" />
                    </div>
                    <div class="composition-legend">
                        <div v-for="part in composition" :key="part.key">
                            <i :style="{ background: part.color }" />
                            <span>{{ part.label }}</span>
                            <strong>{{ money(part.value) }}</strong>
                            <em>{{ percent(part.percent) }}</em>
                        </div>
                    </div>
                    <div v-if="fixedTypes.length" class="caption grey--text mt-2">
                        Fixos: <span v-for="(item, index) in fixedTypes" :key="item.key">{{ index ? ' · ' : '' }}{{ item.label }} {{ money(item.value) }}</span>
                    </div>
                </v-card-text>
            </v-card>

            <v-alert v-if="result.unlinked_vehicles" dense text type="warning" class="body-2">
                Há gastos ligados a {{ result.unlinked_vehicles }} veículo(s) que não estão mais cadastrados; eles não entram na lista.
            </v-alert>

            <v-card>
                <v-data-table :headers="headers" :items="result.rows" :items-per-page="25" :loading="loading"
                    class="app-table hover-table" no-data-text="Nenhum veículo" sort-by="cost" sort-desc
                    :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }" @click:row="openVehicle">
                    <template v-slot:[`item.code`]="{ item }">
                        <strong>{{ item.code }}</strong>
                        <div class="caption grey--text">{{ item.plate }} · {{ [item.brand, item.model].filter(Boolean).join(' ') }}</div>
                    </template>
                    <template v-slot:[`item.fixed`]="{ item }">
                        <span :class="{ 'grey--text': !item.fixed }">{{ money(item.fixed) }}</span>
                    </template>
                    <template v-slot:[`item.fuel`]="{ item }">
                        <span :class="{ 'grey--text': !item.fuel }">{{ money(item.fuel) }}</span>
                    </template>
                    <template v-slot:[`item.maintenance`]="{ item }">
                        <span :class="{ 'grey--text': !item.maintenance }">{{ money(item.maintenance) }}</span>
                    </template>
                    <template v-slot:[`item.partner`]="{ item }">
                        <span :class="{ 'grey--text': !item.partner }">{{ money(item.partner) }}</span>
                    </template>
                    <template v-slot:[`item.other`]="{ item }">
                        <span :class="{ 'grey--text': !item.other }">{{ money(item.other) }}</span>
                    </template>
                    <template v-slot:[`item.revenue`]="{ item }">
                        <span :class="{ 'grey--text': !item.revenue }">{{ money(item.revenue) }}</span>
                    </template>
                    <template v-slot:[`item.cost`]="{ item }">
                        <strong>{{ money(item.cost) }}</strong>
                    </template>
                    <template v-slot:[`item.margin`]="{ item }">
                        <span :class="item.margin < 0 ? 'red--text' : 'green--text text--darken-2'">{{ money(item.margin) }}</span>
                        <div class="caption grey--text">{{ item.margin_percent !== null ? percent(item.margin_percent) : '' }}</div>
                    </template>
                    <template v-slot:[`item.cost_per_km`]="{ item }">
                        {{ item.cost_per_km !== null ? money(item.cost_per_km) : '-' }}
                        <div class="caption grey--text" v-if="item.km">{{ number(item.km) }} km</div>
                    </template>
                </v-data-table>
            </v-card>

            <p class="caption grey--text mt-3">
                Custos fixos são rateados pelos dias do período (anuais divididos em 12). Motorista parceiro usa o valor por
                localidade das viagens finalizadas; salário de funcionário não é atribuído a veículo. Manutenções em rascunho
                ou canceladas não entram. "Outras" são despesas lançadas com o veículo (multas, lavagem...).
            </p>
        </template>
    </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

const PARTS = [
    { key: 'fixed', label: 'Custos fixos', color: '#5b6fd6' },
    { key: 'fuel', label: 'Combustível', color: '#f0a020' },
    { key: 'maintenance', label: 'Manutenção', color: '#d9534f' },
    { key: 'partner', label: 'Motorista parceiro', color: '#2bb3a3' },
    { key: 'other', label: 'Outras despesas', color: '#8a96ab' }
]

export default {
    name: 'VehicleCostsOverview',

    data: () => ({
        loading: false,
        exporting: false,
        result: null,
        filter: {
            start: moment().startOf('month').format('YYYY-MM-DD'),
            end: moment().endOf('month').format('YYYY-MM-DD'),
            search: '',
            onlyActive: true
        },
        headers: [
            { text: 'Veículo', value: 'code' },
            { text: 'Fixos', value: 'fixed', align: 'end' },
            { text: 'Combustível', value: 'fuel', align: 'end' },
            { text: 'Manutenção', value: 'maintenance', align: 'end' },
            { text: 'Parceiro', value: 'partner', align: 'end' },
            { text: 'Outras', value: 'other', align: 'end' },
            { text: 'Custo total', value: 'cost', align: 'end' },
            { text: 'Receita', value: 'revenue', align: 'end' },
            { text: 'Margem', value: 'margin', align: 'end' },
            { text: 'Custo/km', value: 'cost_per_km', align: 'end' }
        ]
    }),

    computed: {
        totals() {
            return this.result ? this.result.totals : {}
        },
        composition() {
            const total = Number(this.totals.cost) || 0
            return PARTS.map(part => ({
                ...part,
                value: this.totals[part.key] || 0,
                percent: total > 0 ? (this.totals[part.key] || 0) / total * 100 : 0
            }))
        },
        fixedTypes() {
            const byType = this.totals.fixed_by_type || {}
            const labels = (this.result && this.result.types) || {}
            return Object.keys(byType).map(key => ({ key, label: labels[key] || key, value: byType[key] }))
        }
    },

    created() {
        this.load()
    },

    methods: {
        params() {
            return { start: this.filter.start, end: this.filter.end, search: this.filter.search || '', only_active: this.filter.onlyActive ? 'true' : 'false' }
        },

        async load() {
            this.loading = true
            try {
                const response = await axios.get('/api/v2/financial/vehicle-costs/overview', { params: this.params() })
                this.result = response.data.content
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.loading = false
            }
        },

        setMonth(offset) {
            const month = moment().add(offset, 'month')
            this.filter.start = month.clone().startOf('month').format('YYYY-MM-DD')
            this.filter.end = month.clone().endOf('month').format('YYYY-MM-DD')
            this.load()
        },

        setYear() {
            this.filter.start = moment().startOf('year').format('YYYY-MM-DD')
            this.filter.end = moment().endOf('month').format('YYYY-MM-DD')
            this.load()
        },

        openVehicle(item) {
            this.$router.push({ path: `/financeiro/veiculos/${item.id}`, query: { start: this.filter.start, end: this.filter.end } })
        },

        async exportOverview() {
            this.exporting = true
            try {
                const response = await axios.get('/api/v2/financial/vehicle-costs/export', { params: this.params(), responseType: 'blob' })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(new Blob([response.data]))
                link.download = `custos-veiculos-${this.filter.start}-a-${this.filter.end}.xlsx`
                link.click()
                URL.revokeObjectURL(link.href)
            } catch (error) {
                this.$dialog.notify.error('Não foi possível exportar', { position: 'top-right', timeout: 4000 })
            } finally {
                this.exporting = false
            }
        },

        money(value) {
            return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },

        percent(value) {
            return `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
        },

        number(value) {
            return Number(value || 0).toLocaleString('pt-BR')
        }
    }
}
</script>

<style lang="scss" scoped>
.composition-bar {
    display: flex;
    height: 14px;
    border-radius: 7px;
    overflow: hidden;
    background: #eef1f6;
    margin-bottom: 12px;
}

.composition-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;

    div {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
    }

    i {
        width: 10px;
        height: 10px;
        border-radius: 3px;
        display: inline-block;
    }

    em {
        color: #8a93a5;
        font-style: normal;
    }
}
</style>
